/**
 * Phone Validation & Normalization for Brazilian Numbers
 * ======================================================
 * Validates Brazilian phone numbers and converts to E.164 format.
 */

/**
 * Clean phone number - remove all non-digits
 */
export function cleanPhone(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * Validate Brazilian phone number
 * Accepts formats with or without country code:
 * - (11) 98765-4321
 * - 11987654321
 * - +5511987654321
 * - 5511987654321
 */
export function isValidBrazilianPhone(phone: string): boolean {
  const digits = cleanPhone(phone);
  
  // Check for valid lengths
  // 10-11 digits: DDD + number (landline or mobile)
  // 12-13 digits: 55 + DDD + number
  if (digits.length < 10 || digits.length > 13) {
    return false;
  }
  
  // Extract the base number (without country code)
  let baseNumber = digits;
  if (digits.startsWith('55') && digits.length >= 12) {
    baseNumber = digits.slice(2);
  }
  
  // Should have 10-11 digits after removing country code
  if (baseNumber.length < 10 || baseNumber.length > 11) {
    return false;
  }
  
  // First 2 digits should be valid DDD (11-99)
  const ddd = parseInt(baseNumber.slice(0, 2), 10);
  if (ddd < 11 || ddd > 99) {
    return false;
  }
  
  // Mobile numbers start with 9 (after DDD)
  // Landlines start with 2-5
  const thirdDigit = baseNumber[2];
  if (!['2', '3', '4', '5', '9'].includes(thirdDigit)) {
    return false;
  }
  
  // Mobile numbers must have 11 digits (DDD + 9 + 8 digits)
  if (thirdDigit === '9' && baseNumber.length !== 11) {
    return false;
  }
  
  return true;
}

/**
 * Convert phone to E.164 format (+5511987654321)
 */
export function toE164(phone: string): string | null {
  if (!isValidBrazilianPhone(phone)) {
    return null;
  }
  
  const digits = cleanPhone(phone);
  
  // Already has country code
  if (digits.startsWith('55') && digits.length >= 12) {
    return `+${digits}`;
  }
  
  // Add country code
  return `+55${digits}`;
}

/**
 * Format phone for display (Brazilian format)
 * +5519987654321 -> (19) 98765-4321
 */
export function formatPhoneForDisplay(phone: string): string {
  const digits = cleanPhone(phone);
  
  // Remove country code if present
  let baseNumber = digits;
  if (digits.startsWith('55') && digits.length >= 12) {
    baseNumber = digits.slice(2);
  }
  
  // Format as (XX) XXXXX-XXXX or (XX) XXXX-XXXX
  if (baseNumber.length === 11) {
    return `(${baseNumber.slice(0, 2)}) ${baseNumber.slice(2, 7)}-${baseNumber.slice(7)}`;
  } else if (baseNumber.length === 10) {
    return `(${baseNumber.slice(0, 2)}) ${baseNumber.slice(2, 6)}-${baseNumber.slice(6)}`;
  }
  
  return phone; // Return original if can't format
}

/**
 * Apply input mask for Brazilian phone
 * Formats as user types: (XX) XXXXX-XXXX
 */
export function applyPhoneMask(value: string): string {
  const digits = cleanPhone(value);
  
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }
  
  // Max 11 digits for display (DDD + 9 digits)
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}
